"use client";

import * as React from "react";
import { Minus, Plus, Tag } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductView() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div className="aspect-square w-full animate-pulse rounded-lg bg-muted" />
        <div className="space-y-4">
          <div className="h-8 w-3/4 animate-pulse rounded-md bg-muted" />
          <div className="h-6 w-1/3 animate-pulse rounded-md bg-muted" />
          <div className="h-24 w-full animate-pulse rounded-md bg-muted" />
          <div className="h-12 w-full animate-pulse rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-center text-muted-foreground">
        Unable to load kitten details. Please try again later.
      </p>
    );
  }

  if (!products || products.length === 0) {
    return (
      <p className="text-center text-muted-foreground">Kitten not found.</p>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price < product.price;

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
      {}
      <div className="lg:sticky lg:top-24 lg:self-start">
        <Card className="overflow-hidden border-border bg-card shadow-lg">
          {product.featured_image_url ? (
            <img
              src={product.featured_image_url}
              alt={product.title}
              loading="lazy"
              decoding="async"
              className="aspect-square w-full object-cover"
            />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center bg-muted text-muted-foreground">
              Image coming soon
            </div>
          )}
        </Card>
      </div>

      {}
      <div className="flex flex-col">
        {product.categories && product.categories.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {product.categories.map((cat, i) => (
              <Link to={cat.link} key={cat.id} data-index={i}>
                <Badge
                  variant="secondary"
                  className="bg-accent text-accent-foreground hover:bg-accent/80"
                >
                  {cat.name}
                </Badge>
              </Link>
            ))}
          </div>
        )}

        <h1
          className="font-default text-3xl font-semibold leading-tight text-foreground sm:text-4xl lg:text-5xl"
          style={{ fontFamily: "var(--font-serif)" }}
        >
          {product.title}
        </h1>

        {}
        <div className="mt-6 flex items-baseline gap-3">
          {hasDiscount ? (
            <>
              <span className="text-3xl font-semibold text-primary sm:text-4xl">
                {"$"}
                {product.discounted_price?.toFixed(2)}
              </span>
              <span className="text-xl text-muted-foreground line-through">
                {"$"}
                {product.price.toFixed(2)}
              </span>
              <Badge className="bg-primary text-primary-foreground">
                Special
              </Badge>
            </>
          ) : (
            <span className="text-3xl font-semibold text-primary sm:text-4xl">
              {"$"}
              {product.price.toFixed(2)}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="mt-6 text-base leading-relaxed text-muted-foreground [&_p]:mb-2 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        <div className="my-8 h-px w-full bg-border" />

        {}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
          <div className="flex-1">
            <Link
              to="https://form.getformly.com/f/mzoi32"
              newTab
              className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-primary px-6 text-base font-medium text-primary-foreground shadow-md transition-colors hover:bg-primary/90"
            >
              Inquire About Adoption
            </Link>
          </div>
        </div>

        {}
        {product.long_description && (
          <Accordion
            type="single"
            collapsible
            defaultValue="description"
            className="mt-10 w-full"
          >
            <AccordionItem
              value="description"
              className="border-b border-border"
            >
              <AccordionTrigger className="font-default text-lg font-semibold text-foreground">
                About This Kitten
              </AccordionTrigger>
              <AccordionContent>
                <div
                  className="prose max-w-none text-muted-foreground [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:text-primary [&_a]:underline [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                  dangerouslySetInnerHTML={{ __html: product.long_description }}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {}
        {((product.categories && product.categories.length > 0) ||
          (product.tags && product.tags.length > 0)) && (
          <div className="mt-8 space-y-3 rounded-lg border border-border bg-card p-5">
            {product.categories && product.categories.length > 0 && (
              <div className="flex flex-wrap items-start gap-2 text-sm">
                <span className="font-medium text-foreground">
                  Breed Category:
                </span>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((cat, i) => (
                    <Link
                      to={cat.link}
                      key={`cat-${cat.id}`}
                      data-index={i}
                      className="text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap items-start gap-2 text-sm">
                <span className="flex items-center gap-1 font-medium text-foreground">
                  <Tag className="size-3.5" />
                  Traits:
                </span>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, i) => (
                    <Link
                      to={tag.link}
                      key={`tag-${tag.id}`}
                      data-index={i}
                      className="text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                    >
                      {"#"}
                      {tag.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Product() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      data-section-id="11984"
      id="single-product-template-section"
      className="bg-background py-16 sm:py-20 lg:py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductView />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}